import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import {
  Carousel,
  ButtonTemplate,
  ConfirmTemplate,
  SummaryTemplate,
  LocationMessage,
  type CarouselItem,
  type ButtonTemplateAction,
  type SummaryTemplateField,
  type CalendarEventData,
} from '@/components/ui/message-types';

// 1. Define the base styles and variants for text messages
const chatMessageVariants = cva(
  // Base classes applied to all messages
 'my-3 mx-2 max-w-[95%] rounded-2xl px-5 py-3 text-base select-text transition-all ease-in-out animate-in fade-in slide-in-from-bottom-4 duration-500 break-words backdrop-blur-sm border-2',
  {
    variants: {
      variant: {
        user: 'self-end bg-gradient-to-br from-[var(--msg-user-bg-from)] to-[var(--msg-user-bg-to)] text-right text-[color:var(--msg-user-text)] border-[color:var(--msg-user-border)] shadow-lg shadow-[color:var(--msg-user-shadow)]',
        assistant: 'self-start bg-gradient-to-br from-[var(--msg-asst-bg-from)] to-[var(--msg-asst-bg-to)] text-left text-[color:var(--msg-asst-text)] border-[color:var(--msg-asst-border)] shadow-lg shadow-[color:var(--msg-asst-shadow)]',
      },
    },
    defaultVariants: {
      variant: 'assistant',
    },
  }
);

// Special message types that render interactive components
export type MessageType = 'text' | 'carousel' | 'button_template' | 'confirm_template' | 'summary_template' | 'location' | 'service_complete';

export interface MessageData {
  // Carousel data
  items?: CarouselItem[];

  // Button template data
  title?: string;
  description?: string;
  actions?: ButtonTemplateAction[];

  // Confirm template data
  message?: string;
  confirmAction?: { label: string; value?: string };
  cancelAction?: { label: string; value?: string };

  // Summary template data
  fields?: SummaryTemplateField[];
  calendarEvent?: CalendarEventData;

  // Location data
  address?: string;
  latitude?: number;
  longitude?: number;
  googleMapsUrl?: string;

  // Service complete data
  goodbyeMessage?: string;
  buttonLabel?: string;
}

export interface DEAIChatMessageProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof chatMessageVariants> {
  isFaded?: boolean;
  messageType?: MessageType;
  messageData?: MessageData;
  onActionClick?: (action: any) => void;
  onConfirm?: (value?: string) => void;
  onCancel?: (value?: string) => void;
  onRestartSession?: () => void;
}

const DEAIChatMessage = React.forwardRef<HTMLDivElement, DEAIChatMessageProps>(
  ({
    className,
    variant,
    isFaded,
    messageType = 'text',
    messageData,
    onActionClick,
    onConfirm,
    onCancel,
    onRestartSession,
    children,
    ...props
  }, ref) => {


    // Render interactive message types
    if (messageType === 'carousel' && messageData?.items) {
      return (
        <div
          ref={ref}
          className={cn(
            'my-3 mx-2 w-[calc(100%-1rem)] max-w-full self-start animate-in fade-in slide-in-from-bottom-4 duration-500 overflow-hidden',
            { 'opacity-0 duration-[3000ms]': isFaded },
            className
          )}
          {...props}
        >
          <Carousel
            items={messageData.items}
            onItemClick={(item, index) => onActionClick?.({ type: 'carousel', item, index })}
          />
        </div>
      );
    }

    if (messageType === 'button_template' && messageData?.actions) {
      return (
        <div
          ref={ref}
          className={cn(
            'my-3 mx-2 max-w-[95%] self-start animate-in fade-in slide-in-from-bottom-4 duration-500',
            { 'opacity-0 duration-[3000ms]': isFaded },
            className
          )}
          {...props}
        >
          <ButtonTemplate
            title={messageData.title}
            description={messageData.description}
            actions={messageData.actions}
            onActionClick={(action) => onActionClick?.(action)}
            layout="grid"
            maxColumns={2}
          />
        </div>
      );
    }

    if (messageType === 'confirm_template' && messageData?.message) {
      return (
        <div
          ref={ref}
          className={cn(
            'my-3 mx-2 max-w-[95%] self-start animate-in fade-in slide-in-from-bottom-4 duration-500',
            { 'opacity-0 duration-[3000ms]': isFaded },
            className
          )}
          {...props}
        >
          <ConfirmTemplate
            message={messageData.message}
            confirmAction={messageData.confirmAction}
            cancelAction={messageData.cancelAction}
            onConfirm={onConfirm || (() => {})}
            onCancel={onCancel || (() => {})}
          />
        </div>
      );
    }

    if (messageType === 'summary_template' && messageData?.title && messageData?.fields) {
      return (
        <div
          ref={ref}
          className={cn(
            'my-3 mx-2 max-w-[95%] self-start animate-in fade-in slide-in-from-bottom-4 duration-500',
            { 'opacity-0 duration-[3000ms]': isFaded },
            className
          )}
          {...props}
        >
          <SummaryTemplate
            title={messageData.title}
            fields={messageData.fields}
            calendarEvent={messageData.calendarEvent}
          />
        </div>
      );
    }

    if (messageType === 'service_complete') {
      return (
        <div
          ref={ref}
          className={cn(
            'my-3 mx-2 max-w-[95%] self-start animate-in fade-in slide-in-from-bottom-4 duration-500',
            { 'opacity-0 duration-[3000ms]': isFaded },
            className
          )}
          {...props}
        >
          <div className="bg-gradient-to-br from-[var(--msg-asst-bg-from)] to-[var(--msg-asst-bg-to)] rounded-2xl px-5 py-4 border-2 border-[color:var(--msg-asst-border)] shadow-lg shadow-[color:var(--msg-asst-shadow)] backdrop-blur-sm">
            {/* Goodbye message */}
            <p className="text-[color:var(--msg-asst-text)] mb-4 leading-relaxed">
              {children || messageData?.goodbyeMessage || "Thanks for chatting! Feel free to start a new conversation anytime."}
            </p>
            {/* Start New Session button */}
            <button
              onClick={onRestartSession}
              className="w-full py-3 px-4 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-medium rounded-xl transition-all duration-200 shadow-md hover:shadow-lg active:scale-[0.98]"
            >
              {messageData?.buttonLabel || "Start New Session"}
            </button>
          </div>
        </div>
      );
    }

    if (messageType === 'location' && messageData?.address) {
      return (
        <div
          ref={ref}
          className={cn(
            'my-3 mx-2 max-w-full self-start animate-in fade-in slide-in-from-bottom-4 duration-500',
            { 'opacity-0 duration-[3000ms]': isFaded },
            className
          )}
          {...props}
        >
          <LocationMessage
            title={messageData.title || "Location"}
            address={messageData.address}
            latitude={messageData.latitude}
            longitude={messageData.longitude}
            onNavigate={(lat, lng) => {
              if (messageData.googleMapsUrl) {
                window.open(messageData.googleMapsUrl, '_blank');
              } else if (lat && lng) {
                window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`, '_blank');
              } else {
                window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(messageData.address!)}`, '_blank');
              }
            }}
          />
        </div>
      );
    }

    // Default text message rendering
    return (
      <div
        ref={ref}
        className={cn(
            chatMessageVariants({ variant }),
            {
              'opacity-0 duration-[3000ms]': isFaded,
            },
            className
          )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

DEAIChatMessage.displayName = 'DEAIChatMessage';

export { DEAIChatMessage, chatMessageVariants };